
import { Component, Prop, Vue, VModel, Watch } from 'vue-property-decorator';
import TextInput from './TextInput.vue';
import TextArea from './TextArea.vue';
import DatePicker from './DatePicker.vue';
import DateTimePicker from './DateTimePicker.vue';
import NSelect from './NSelect.vue';

@Component({
    components: { TextInput, TextArea, DatePicker, DateTimePicker, NSelect },
})
export default class CustomFieldGroupForm extends Vue {
    @Prop({ type: Array, required: true }) groups!: any[];
    @VModel({ type: Array, required: true }) innerValue!: any[];
    @Prop({ default: false }) readonly!: boolean;

    get isTablet(): boolean {
        return this.$vuetify.breakpoint.width >= 576 && this.$vuetify.breakpoint.width < 991;
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 576;
    }

    getFields(group: any) {
        return group.auditCustomFields || group.categoryCustomFields || [];
    }

    getField(group: any, index: number) {
        return this.getFields(group)[index];
    }

    getRequiredRules(field: any): Array<(value: any) => boolean | string> {
        const rules: Array<(value: any) => boolean | string> = [];

        // Pravidlo pro povinnost
        if (field.isRequired) {
            rules.push((value) => (value !== null && value !== undefined && value !== '') || 'Toto pole je povinné.');
        }

        // Přidání pravidla pro celá čísla
        if (field.type === 'Integer') {
            rules.push((value) => Number.isInteger(Number(value)) || 'Hodnota musí být celé číslo.');
        }

        return rules;
    }

    getInputComponent(field: any) {
        switch (field.type) {
            case 'String':
                return 'TextInput';
            case 'Integer':
            case 'Double':
                return 'TextInput';
            case 'Date':
                return 'DatePicker';
            case 'DateTime':
                return 'DateTimePicker';
            case 'Boolean':
                return 'NSelect';
            default:
                return 'TextInput';
        }
    }
    getInputType(field: any) {
        switch (field.type) {
            case 'Integer':
                return 'number';
            case 'Double':
                return 'number';
            case 'String':
                return 'text';
            default:
                return undefined;
        }
    }

    getFlatValue(group: any, field: any) {
        const groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id) || {};
        const val = (groupVal.planItemCustomValues || []).find(
            (v: any) => v.customFieldId === field.id && (v.rowNumber == null || v.rowNumber === undefined)
        );
        return val ? val.value : '';
    }
    setFlatValue(group: any, field: any, v: any) {
        let groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id);
        if (!groupVal) {
            groupVal = {
                customFieldGroupId: group.id,
                planItemCustomValues: [],
                name: group.name,
                position: group.position,
                customFieldGroupType: group.customFieldGroupType,
            };
            this.innerValue?.push(groupVal);
        }
        let val = groupVal.planItemCustomValues.find(
            (x: any) => x.customFieldId === field.id && (x.rowNumber == null || x.rowNumber === undefined)
        );
        if (!val) {
            val = {
                customFieldId: field.id,
                type: field.type,
                value: v,
                name: field.name,
                position: field.position,
            };
            groupVal.planItemCustomValues.push(val);
        } else {
            val.value = v;
        }
        this.$emit('input', this.innerValue);
    }
    getArrayRows(group: any) {
        const groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id);
        if (!groupVal || !groupVal.planItemCustomValues || groupVal.planItemCustomValues.length === 0) {
            // Pokud není žádný řádek, přidej jeden prázdný řádek
            let targetGroup = groupVal;
            if (!targetGroup) {
                targetGroup = {
                    customFieldGroupId: group.id,
                    planItemCustomValues: [],
                    name: group.name,
                    position: group.position,
                    customFieldGroupType: group.customFieldGroupType,
                };
                this.innerValue?.push(targetGroup);
            }
            for (const field of this.getFields(group)) {
                targetGroup.planItemCustomValues.push({
                    customFieldId: field.id,
                    type: field.type,
                    value: '',
                    rowNumber: 0,
                    name: field.name,
                    position: field.position,
                });
            }
            this.$emit('input', [...this.innerValue]);
            return [0];
        }
        // Získat unikátní rowNumber, které skutečně existují
        const rowNumbers = Array.from(new Set(groupVal.planItemCustomValues.map((v: any) => v.rowNumber)));
        return rowNumbers.sort((a, b) => Number(a) - Number(b));
    }
    getArrayValue(group: any, field: any, rowIdx: number) {
        const groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id) || {};
        const val = (groupVal.planItemCustomValues || []).find(
            (v: any) => v.customFieldId === field.id && v.rowNumber === rowIdx
        );
        return val ? val.value : '';
    }
    setArrayValue(group: any, field: any, rowIdx: number, v: any) {
        let groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id);
        if (!groupVal) {
            groupVal = { customFieldGroupId: group.id, planItemCustomValues: [] };
            this.innerValue?.push(groupVal);
        }
        let val = groupVal.planItemCustomValues.find((x: any) => x.customFieldId === field.id && x.rowNumber === rowIdx);
        if (!val) {
            val = {
                customFieldId: field.id,
                type: field.type,
                value: v,
                rowNumber: rowIdx,
                name: field.name,
                position: field.position,
            };
            groupVal.planItemCustomValues.push(val);
        } else {
            val.value = v;
        }
        this.$emit('input', this.innerValue);
    }
    addArrayRow(group: any) {
        let groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id);
        if (!groupVal) {
            groupVal = {
                customFieldGroupId: group.id,
                planItemCustomValues: [],
                name: group.name,
                position: group.position,
                customFieldGroupType: group.customFieldGroupType,
            };
            this.innerValue?.push(groupVal);
        }
        const rows = this.getArrayRows(group);
        const newRow = rows.length;
        // Přidání prázdných hodnot pro každý field v novém řádku
        for (const field of this.getFields(group)) {
            groupVal.planItemCustomValues.push({
                customFieldId: field.id,
                type: field.type,
                value: '',
                rowNumber: newRow,
                name: field.name,
                position: field.position,
            });
        }
        this.$emit('input', [...this.innerValue]);
    }
    removeArrayRow(group: any, rowIdx: number) {
        let groupVal = this.innerValue?.find((g: any) => g.customFieldGroupId === group.id);
        if (!groupVal) return;
        // Odstranit všechny hodnoty s daným rowIdx
        groupVal.planItemCustomValues = groupVal.planItemCustomValues.filter((v: any) => v.rowNumber !== rowIdx);
        // Přečíslovat zbylé položky podle nového pořadí řádků
        const rowNumbers = Array.from(new Set(groupVal.planItemCustomValues.map((v: any) => v.rowNumber))).sort(
            (a, b) => Number(a) - Number(b)
        );
        rowNumbers.forEach((oldRow, newRow) => {
            groupVal.planItemCustomValues.forEach((v: any) => {
                if (v.rowNumber === oldRow) v.rowNumber = newRow;
            });
        });
        this.$emit('input', [...this.innerValue]);
    }
}
